import React from 'react';

import { Box, Button, Grid } from '@mui/material';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router';

import { DeepPartial } from '@hrlogics/helper-types';
import { Field, Form, FormApi, FormSpy } from '@hrlogics/react-components/form';

import { appRoutes, DateFormatPatterns, RouterEntitiesId, SSN_MASK } from '@clearverify/constants';

import {
  InboxItemDocument,
  InboxItemsScrollableListDocument,
  useCreateVerificationRequestMutation,
  VerificationRequestsReportListDocument,
} from 'src/api';
import { DateField, MaskedTextField, TextField } from 'src/components/finalForm';
import {
  ConsumerFreezeErrorBox,
  VerificationRequestCreateByVerifierModalFormConsumerErrorBox,
  VerificationRequestCreateByVerifierModalFormErrorBox,
  VerificationRequestCreateFormPermissiblePurposeField,
  VerificationRequestCreateFormVerificationReportTypeField,
  VerificationRequestCreateModalFormEmployerField,
} from 'src/components/verificationRequest';
import {
  commercialVerificationRequestTypeOptions,
  VerificationRequestCreateFormFields,
} from 'src/constants/verificationRequest';
import { useNotification } from 'src/hooks/app';
import { useInboxItemsQueryVariables } from 'src/hooks/inboxItem';
import { useVerificationRequestFormData } from 'src/hooks/verificationRequest';
import { useCurrentUserVerifier } from 'src/hooks/verifier';
import { convertEmployerListItemsToEmployerDataItems } from 'src/utils/employer';
import { buildUrl } from 'src/utils/router';
import { validateWithYupSchema } from 'src/utils/yup';

import { VerificationRequestCreatePaymentBlock } from './components';
import { VerificationRequestCreateFormValues } from './types';
import { verificationRequestCreateValidationSchema } from './utils';

interface VerificationRequestCreateByCommercialVerifierModalFormProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const VerificationRequestCreateByCommercialVerifierModalForm: React.FC<
  VerificationRequestCreateByCommercialVerifierModalFormProps
> = ({ closeModal, switchClosability }) => {
  const navigate = useNavigate();

  const [needToRedirect, setNeedToRedirect] = React.useState(false);

  const { createApolloContext, spawnNotification } = useNotification();

  const inboxItemsQueryVariables = useInboxItemsQueryVariables();

  const { isFormInfoLoading, resetData, startSubmitting, stopSubmitting } =
    useVerificationRequestFormData();

  // biome-ignore lint/correctness/useExhaustiveDependencies: only reset data on unmount
  React.useEffect(() => {
    return () => resetData();
  }, []);

  const { verifierId } = useCurrentUserVerifier();

  const [createVerificationRequest] = useCreateVerificationRequestMutation({
    context: createApolloContext({
      errorMessage: 'Error creating verification request',
    }),
    refetchQueries: [
      InboxItemsScrollableListDocument,
      InboxItemDocument,
      VerificationRequestsReportListDocument,
    ],
  });

  const minDate = React.useMemo(() => DateTime.fromISO('1800-01-01'), []);
  const maxDate = React.useRef(DateTime.now());

  const handleSubmit = React.useCallback(
    async (
      formValues: VerificationRequestCreateFormValues,
      formApi: FormApi<
        VerificationRequestCreateFormValues,
        DeepPartial<VerificationRequestCreateFormValues>
      >,
    ) => {
      formApi.change(VerificationRequestCreateFormFields.error, null);

      const employeeDateOfBirth = DateTime.fromISO(formValues.employeeDateOfBirth);
      if (!employeeDateOfBirth.isValid) return;

      startSubmitting();
      switchClosability(false);

      let success = false;
      let inboxItemIdToRedirect: string | null = null;

      try {
        const { data } = await createVerificationRequest({
          variables: {
            ssn: formValues.ssn,
            employeeFirstName: formValues.employeeFirstName,
            employeeLastName: formValues.employeeLastName,
            employeeBirthDate: employeeDateOfBirth.toISODate(),
            requestType: formValues.verificationReportType,
            permissiblePurposeId: String(formValues.permissiblePurposeId),
            paymentMethodId: formValues.paymentMethod,
            noteToEmployee: formValues.noteToEmployee,
            companies: convertEmployerListItemsToEmployerDataItems(formValues.employers),
          },
          refetchQueries: [
            { query: InboxItemsScrollableListDocument, variables: inboxItemsQueryVariables },
          ],
        });

        const redirectUrl = data?.createVerificationRequest.redirectUrl;
        if (redirectUrl) {
          setNeedToRedirect(true);
          window.location.replace(redirectUrl);
          return;
        }

        success = true;

        if (data?.createVerificationRequest.inboxItemId) {
          inboxItemIdToRedirect = data.createVerificationRequest.inboxItemId;
        }

        spawnNotification('Verification Request successfully created', 'success');

        switchClosability(true);
      } finally {
        stopSubmitting();
        switchClosability(true);
        if (success) {
          closeModal();
        }
      }

      if (inboxItemIdToRedirect) {
        navigate(
          buildUrl(appRoutes.inbox.inboxItem, {
            [RouterEntitiesId.inboxItem]: inboxItemIdToRedirect,
          }),
        );
      }
    },
    [
      switchClosability,
      startSubmitting,
      createVerificationRequest,
      inboxItemsQueryVariables,
      stopSubmitting,
      spawnNotification,
      closeModal,
      navigate,
    ],
  );

  return (
    <Form<VerificationRequestCreateFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      subscription={{ valid: true, submitting: true }}
      validate={validateWithYupSchema(verificationRequestCreateValidationSchema)}
    >
      {({ submitting, valid }) => {
        return (
          <React.Fragment>
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12 }}>
                <Field
                  name={VerificationRequestCreateFormFields.ssn}
                  component={MaskedTextField}
                  label="SSN"
                  mask={SSN_MASK}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Field
                  name={VerificationRequestCreateFormFields.employeeFirstName}
                  component={TextField}
                  label="Employee First Name"
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Field
                  name={VerificationRequestCreateFormFields.employeeLastName}
                  component={TextField}
                  label="Employee Last Name"
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Field
                  name={VerificationRequestCreateFormFields.employeeDateOfBirth}
                  component={DateField}
                  minDate={minDate}
                  maxDate={maxDate.current}
                  inputFormat={DateFormatPatterns.shortDateWithSlash}
                  defaultValue={DateFormatPatterns.shortDateWithSlash}
                  label="Employee Date of Birth"
                  required
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormSpy subscription={{ values: true, errors: true }}>
                  {() => <VerificationRequestCreateModalFormEmployerField />}
                </FormSpy>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <VerificationRequestCreateFormVerificationReportTypeField
                  options={commercialVerificationRequestTypeOptions}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <VerificationRequestCreateFormPermissiblePurposeField verifierId={verifierId} />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Field
                  name={VerificationRequestCreateFormFields.noteToEmployee}
                  component={TextField}
                  label="Note to Employee (optional)"
                  fullWidth
                />
              </Grid>
            </Grid>

            <VerificationRequestCreatePaymentBlock />
            <VerificationRequestCreateByVerifierModalFormErrorBox />

            <FormSpy subscription={{ values: true, errors: true }}>
              {() => <VerificationRequestCreateByVerifierModalFormConsumerErrorBox />}
            </FormSpy>

            <ConsumerFreezeErrorBox />

            <Box display="flex" gap={2} mt={4} justifyContent="center">
              <Button
                variant="outlined"
                color="error"
                onClick={() => closeModal()}
                disabled={submitting || isFormInfoLoading || needToRedirect}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={!valid || submitting || isFormInfoLoading || needToRedirect}
                loading={submitting || isFormInfoLoading}
              >
                Submit request
              </Button>
            </Box>
          </React.Fragment>
        );
      }}
    </Form>
  );
};

const initialValues: Partial<VerificationRequestCreateFormValues> = {
  [VerificationRequestCreateFormFields.verificationReportType]:
    commercialVerificationRequestTypeOptions.at(0)?.value,
  [VerificationRequestCreateFormFields.employers]: [],
  [VerificationRequestCreateFormFields.error]: null,
};
